import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { kommunikationApi } from '../api';

const STATUS_OPTIONEN = ['Entwurf','geplant','versendet','zugestellt','empfangen','gelesen','beantwortet','Antwort ausstehend','fehlgeschlagen','archiviert'];
const STATUS_FARBEN = {
  Entwurf: '#64748b', geplant: '#f59e0b', versendet: '#3b82f6', zugestellt: '#22c55e',
  empfangen: '#10b981', gelesen: '#6366f1', beantwortet: '#22c55e', 'Antwort ausstehend': '#f97316',
  fehlgeschlagen: '#ef4444', archiviert: '#374151'
};

export default function KommunikationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [komm, setKomm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [neueAntwort, setNeueAntwort] = useState('');
  const [sendingAntwort, setSendingAntwort] = useState(false);
  const [statusAktualisieren, setStatusAktualisieren] = useState(false);
  const [neuerStatus, setNeuerStatus] = useState('');

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const k = await kommunikationApi.get(id);
      setKomm(k);
      setNeuerStatus(k.status);
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const handleStatusUpdate = async () => {
    try {
      await kommunikationApi.updateStatus(id, neuerStatus);
      setStatusAktualisieren(false);
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const handleAntwort = async () => {
    if (!neueAntwort.trim()) return;
    setSendingAntwort(true);
    try {
      await kommunikationApi.addNachricht(id, { inhalt: neueAntwort, richtung: 'ausgehend' });
      setNeueAntwort('');
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    finally { setSendingAntwort(false); }
  };

  const handleArchivieren = async () => {
    if (!window.confirm('Eintrag archivieren?')) return;
    try {
      await kommunikationApi.updateStatus(id, 'archiviert');
      navigate('/kommunikation');
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6366f1' }}>⏳ Lade…</div>;
  if (err) return <div style={{ padding: 20, color: '#f87171' }}>{err}</div>;
  if (!komm) return null;

  const istUeberfaellig = komm.antwort_faellig_am && komm.antwort_status === 'offen' && new Date(komm.antwort_faellig_am) < new Date();

  return (
    <div style={{ padding: 20, color: '#f1f5f9', maxWidth: 900 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{komm.betreff || '(kein Betreff)'}</h1>
            <span style={{ background: STATUS_FARBEN[komm.status] || '#334155', color: '#fff', fontSize: 11, padding: '2px 8px', borderRadius: 4 }}>{komm.status}</span>
          </div>
          <div style={{ display: 'flex', gap: 12, color: '#64748b', fontSize: 13, flexWrap: 'wrap' }}>
            <span>📅 {komm.datum ? new Date(komm.datum).toLocaleString('de-DE') : '—'}</span>
            <span>📡 {komm.kanal}</span>
            {komm.richtung && <span>{komm.richtung === 'eingehend' ? '📨 Eingehend' : komm.richtung === 'ausgehend' ? '📤 Ausgehend' : '📝 Intern'}</span>}
            {komm.anbieter_name && <span>🏢 {komm.anbieter_name}</span>}
            {komm.vertrag_titel && <span>📋 {komm.vertrag_titel}</span>}
          </div>
          {istUeberfaellig && (
            <div style={{ marginTop: 8, background: '#450a0a', border: '1px solid #ef4444', borderRadius: 6, padding: '6px 12px', fontSize: 13, color: '#fca5a5' }}>
              ⚠️ Antwortfrist überschritten: {new Date(komm.antwort_faellig_am).toLocaleDateString('de-DE')}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setStatusAktualisieren(!statusAktualisieren)} className="btn-secondary">Status ändern</button>
          {komm.status !== 'archiviert' && <button onClick={handleArchivieren} className="btn-secondary">Archivieren</button>}
          <button onClick={() => navigate('/kommunikation')} className="btn-ghost">← Zurück</button>
        </div>
      </div>

      {/* Status ändern */}
      {statusAktualisieren && (
        <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 14, marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={neuerStatus} onChange={e => setNeuerStatus(e.target.value)} style={sel}>
            {STATUS_OPTIONEN.map(s => <option key={s}>{s}</option>)}
          </select>
          <button onClick={handleStatusUpdate} className="btn-primary">Speichern</button>
          <button onClick={() => setStatusAktualisieren(false)} className="btn-secondary">Abbrechen</button>
        </div>
      )}

      {/* Inhalt */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 20, marginBottom: 16 }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>Inhalt</h2>
        {komm.absender && <div style={{ fontSize: 13, marginBottom: 6 }}><span style={{ color: '#64748b' }}>Von:</span> {komm.absender}</div>}
        {komm.empfaenger && <div style={{ fontSize: 13, marginBottom: 12 }}><span style={{ color: '#64748b' }}>An:</span> {komm.empfaenger}</div>}
        {komm.inhalt ? (
          <div style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{komm.inhalt}</div>
        ) : (
          <div style={{ color: '#475569', fontSize: 13, fontStyle: 'italic' }}>(Kein Inhalt)</div>
        )}
        {komm.anhang_pfad && <div style={{ marginTop: 12, color: '#64748b', fontSize: 13 }}>📎 {komm.anhang_pfad}</div>}
      </div>

      {/* Thread-Nachrichten */}
      {(komm.nachrichten || []).length > 0 && (
        <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 20, marginBottom: 16 }}>
          <h2 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>
            Thread ({komm.nachrichten.length})
          </h2>
          <div style={{ display: 'grid', gap: 10 }}>
            {komm.nachrichten.map(n => (
              <div key={n.id} style={{
                background: '#0f172a', borderRadius: 8, padding: '10px 14px',
                borderLeft: `3px solid ${n.richtung === 'eingehend' ? '#3b82f6' : '#6366f1'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: n.richtung === 'eingehend' ? '#60a5fa' : '#818cf8' }}>
                    {n.richtung === 'eingehend' ? '📨 Eingehend' : '📤 Ausgehend'}
                  </span>
                  <span style={{ fontSize: 12, color: '#475569' }}>
                    {n.erstellt_am ? new Date(n.erstellt_am).toLocaleString('de-DE') : '—'}
                  </span>
                </div>
                <div style={{ color: '#94a3b8', fontSize: 13, whiteSpace: 'pre-wrap' }}>{n.inhalt}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Antwort verfassen */}
      {komm.status !== 'archiviert' && (
        <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 20 }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>Nachricht hinzufügen</h2>
          <textarea
            value={neueAntwort} onChange={e => setNeueAntwort(e.target.value)}
            placeholder="Nachricht eingeben…" rows={4}
            style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', padding: '10px', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box', resize: 'vertical', marginBottom: 10 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleAntwort} disabled={sendingAntwort || !neueAntwort.trim()} className="btn-primary">
              {sendingAntwort ? '⏳…' : '📤 Nachricht speichern'}
            </button>
          </div>
          <p style={{ color: '#475569', fontSize: 12, marginTop: 8 }}>
            Nachrichten werden intern gespeichert. Kein automatischer Versand — Freigabe erforderlich.
          </p>
        </div>
      )}
    </div>
  );
}

const sel = { background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', padding: '8px 10px', borderRadius: 6, fontSize: 13 };
